import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faEdit, faSave, faSpinner, faUser, faBarcode,
  faTags, faInfoCircle, faShoppingCart, faTruck,
  faEuroSign, faAppleAlt, faRulerCombined, faFish
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../ui/ToastProvider';
import { getSupplierArticleById, updateSupplierArticle, createSupplierArticle } from '../../services/supplierApi';
import articleWorkflowApi from '../../services/articleWorkflowApi';
import { SupplierArticle, ArticleStatus } from '../../types/article';
import { PACKAGING_UNITS, CONTENT_UNITS } from '../../constants/articleOptions';
import ArticleDetailVertrieb from '../articles/ArticleDetailVertrieb';
import ArticleDetailMarketing from '../articles/ArticleDetailMarketing';

import ArticleDetailNaehrwerte from '../articles/ArticleDetailNaehrwerte';
import ArticleDetailEinheiten from '../articles/ArticleDetailEinheiten';
import { ProductDataTab } from '../articles/ArticleDetailEinkauf';
import { FischTab } from '../articles/ArticleDetailFisch';
import { useGlobalData } from '../../contexts/GlobalDataContext';

interface SupplierArticleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  article: any; // Article from supplier dashboard
  onSave?: (updatedArticle: any) => void;
}

// **UTILITY FUNCTIONS FOR ENHANCED CHANGE DETECTION**
const getFieldValue = (obj: any, path: string) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

const isFieldChanged = (fieldPath: string, originalData: any, currentData: any): boolean => {
  if (!originalData || !currentData) return false;
  
  const originalValue = getFieldValue(originalData, fieldPath);
  const currentValue = getFieldValue(currentData, fieldPath);
  
  // Handle null/undefined cases
  if (!originalValue && !currentValue) return false;
  if (!originalValue && currentValue) return true;
  if (originalValue && !currentValue) return true;
  
  // For arrays
  if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
    if (originalValue.length !== currentValue.length) return true;
    return originalValue.some((item, index) => 
      JSON.stringify(item) !== JSON.stringify(currentValue[index])
    );
  }
  
  // For objects
  if (typeof originalValue === 'object' && typeof currentValue === 'object') {
    return JSON.stringify(originalValue) !== JSON.stringify(currentValue);
  }
  
  // For primitive values
  return originalValue !== currentValue;
};

const isFieldNew = (fieldPath: string, originalData: any, currentData: any): boolean => {
  if (!originalData || !currentData) return false;
  
  const originalValue = getFieldValue(originalData, fieldPath);
  const currentValue = getFieldValue(currentData, fieldPath);
  
  // Field is new if original was empty/null and current has a value
  const originalEmpty = !originalValue || originalValue === '' || originalValue === null;
  const currentHasValue = currentValue && currentValue !== '' && currentValue !== null;
  
  return originalEmpty && currentHasValue;
};

// Check if any data has changed
const hasDataChanged = (originalData: any, currentData: any): boolean => {
  if (!originalData || !currentData) return false;
  return JSON.stringify(originalData) !== JSON.stringify(currentData);
};

// Get list of modified field paths
const getModifiedFields = (originalData: any, currentData: any): Record<string, boolean> => {
  const modified: Record<string, boolean> = {};
  
  if (!originalData || !currentData) return modified;
  
  const checkFields = (original: any, current: any, path: string = '') => {
    if (!original && !current) return;
    
    if (typeof original === 'object' && typeof current === 'object' && 
        !Array.isArray(original) && !Array.isArray(current) &&
        original !== null && current !== null) {
      // Handle nested objects
      const allKeys = new Set([...Object.keys(original), ...Object.keys(current)]);
      allKeys.forEach(key => {
        const newPath = path ? `${path}.${key}` : key;
        checkFields(original[key], current[key], newPath);
      });
    } else {
      // Compare values
      if (JSON.stringify(original) !== JSON.stringify(current)) {
        modified[path] = true;
      }
    }
  };
  
  checkFields(originalData, currentData);
  return modified;
};

// **ENHANCED FIELD CHANGE INDICATOR - DISABLED FOR SUPPLIER PORTAL**
// Supplier Portal zeigt keine Änderungsmarkierungen an - nur für Admin Portal
interface SupplierFieldHistoryProps {
  fieldName: string;
  fieldPath: string;
  originalData: any;
  currentData: any;
  children: React.ReactNode;
}

const SupplierFieldWithHistory: React.FC<SupplierFieldHistoryProps> = ({ 
  fieldName, 
  fieldPath, 
  originalData, 
  currentData, 
  children 
}) => {
  // **IM SUPPLIER PORTAL: KEINE ÄNDERUNGSMARKIERUNGEN**
  // Supplier sehen ihre eigenen Eingaben ohne Change-Tracking
  return <>{children}</>;
};

type TabType = 'einkauf' | 'naehrwerte' | 'fisch' | 'einheiten' | 'vertrieb' | 'marketing';

const SupplierArticleDetailModal: React.FC<SupplierArticleDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  article,
  onSave 
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('einkauf');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [data, setData] = useState<SupplierArticle | null>(null);
  const [currentData, setCurrentData] = useState<SupplierArticle | null>(null);
  const [originalData, setOriginalData] = useState<SupplierArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useToast();
  
  // Use global data context instead of loading individually
  const { 
    ecrCodes, 
    brands, 
    labels, 
    dropdownData, 
    isLoading: globalDataLoading,
    isReady: globalDataReady 
  } = useGlobalData();

  useEffect(() => {
    if (isOpen && article) {
      // Load article immediately, don't wait for global data
      loadArticleData();
    }
  }, [isOpen, article]);

  // Manage body overflow when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadArticleData = async () => {
    if (!article) return;

    // Handle new article creation
    if (article._id === 'new') {
      console.log('[SupplierArticleDetailModal] Creating new article template...');
      
      // Get supplier information from session to auto-fill kreditor number
      const supplierInfoStr = sessionStorage.getItem('supplierInfo');
      let supplierKreditorNumber = '';
      
      console.log('[SupplierArticleDetailModal] Raw supplier info from session:', supplierInfoStr);
      
      if (supplierInfoStr) {
        try {
          const supplierInfo = JSON.parse(supplierInfoStr);
          console.log('[SupplierArticleDetailModal] Parsed supplier info:', supplierInfo);
          supplierKreditorNumber = supplierInfo.kreditorNumber || '';
          console.log('[SupplierArticleDetailModal] Auto-filling kreditor number:', supplierKreditorNumber);
        } catch (error) {
          console.warn('[SupplierArticleDetailModal] Failed to parse supplier info:', error);
        }
      } else {
        console.warn('[SupplierArticleDetailModal] No supplier info found in session storage');
      }
      
      // Create a new article template with proper structure
      const newArticleTemplate: SupplierArticle = {
        _id: 'new',
        articleNumber: '', // Will be generated on server
        supplierAssignedId: '',
        externalArticleNumber: '',
        name: '',
        description: '',
        description2: '',
        brand: '',
        gtin: '',
        content: undefined,
        contentUnit: '',
        unit: '',
        alcoholContent: undefined,
        itemNumber: '',
        intrastatNumber: '',
        mainSupplierCode: '',
        mainSupplierGLN: '',
        supplierContact: '',
        creditorNumber: supplierKreditorNumber, // Auto-filled from supplier session
        supplierCodeId: '',
        parentId: '',
        goldenRecord: false,
        productCompletePct: undefined,
        trafficLightStatus: '',
        itemType: '',
        itemNumberCheckDigit: '',
        productLine: '',
        pieceWeightArticle: undefined,
        exchangeInformation: '',
        basePriceContent: '',
        baseUnit: '',
        baseComparisonUnitCode: '',
        minTermProduction: '',
        minTermIncoming: '',
        minTermOutgoing: '',
        gpcCategory: '',
        departmentCode: '',
        unitSwap: '',
        barcodeSwap: '',
        orderUnitSwap: '',
        storageZone: '',
        stockTempMin: undefined,
        stockTempMax: undefined,
        countryOfOrigin: '',
        originCountryTrue: '',
        primaryIngredient: '',
        labels: [],
        labelsString: '',
        reusableCode: '',
        netContent: undefined,
        netContentUom: '',
        drainedWeight: undefined,
        drainedWeightUom: '',
        meatOriginAT: false,
        nutrientContentSwap: '',
        allergenTypeSwap: '',
        vePriceUnitConversionFactor: undefined,
        targetGroupMarketing: '',
        ghpApproval: false,
        approvedERP: false,
        approvedPrint: false,
        approvedWeb: false,
        bcBlocked: false,
        bcItemDescription: '',
        bcSearchDescription: '',
        ecrLevel1: '',
        ecrLevel2: '',
        manufacturerName: '',
        manufacturerGLN: '',
        contactName: '',
        contactAddress: '',
        scientificName: '',
        productionMethod: '',
        thawingInstructions: '',
        lmivData: {
          ingredients: '',
          allergens: [],
          nutritionalValues: {
            energy: {},
            fat: {},
            carbohydrates: {}
          },
          additives: [],
          origin: '',
          storage: '',
          usageInstructions: ''
        },
        units: [],
        barcodes: [],
        status: ArticleStatus.DRAFT,
        internalNotes: [],
        supplierNotes: [],
        missingFields: [],
        requestedFields: [],
        changeLog: [],
        reminderCount: 0,
        lastModifiedBy: 'supplier',
        createdBy: 'supplier',
        supplierId: '', // Will be set from session
        completionPercentage: 0, // Initialize with 0% completion
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('[SupplierArticleDetailModal] Final template with kreditor number:', supplierKreditorNumber);
      
      setData(newArticleTemplate);
      setOriginalData(newArticleTemplate);
      setCurrentData(newArticleTemplate);
      setMode('edit'); // Start in edit mode for new articles
      
      console.log('[SupplierArticleDetailModal] New article data initialized:', {
        creditorNumber: newArticleTemplate.creditorNumber,
        mode: 'edit',
        hasKreditorNumber: !!newArticleTemplate.creditorNumber
      });
      
      return;
    }

    // Handle existing article
    setLoading(true);
    setError(null);

    try {
      console.log('[SupplierArticleDetailModal] Loading article data...');
      
      // Get supplier portal tokens
      const sessionToken = sessionStorage.getItem('supplierPortalSession');
      const masterToken = window.location.pathname.split('/')[3]; // Extract from URL path
      
      if (!sessionToken || !masterToken) {
        throw new Error('Supplier portal session not found');
      }
      
      const articleData = await getSupplierArticleById(masterToken, sessionToken, article._id);

      console.log('[SUPPLIER MODAL] Article data loaded:', articleData?.articleNumber);
      console.log('[SUPPLIER MODAL] Images found:', {
        mainImage: articleData?.images?.mainImage ? 'Present' : 'Missing',
        backImages: articleData?.images?.backImages?.length || 0,
        labelImages: articleData?.images?.labelImages?.length || 0
      });

      setData(articleData);
      setOriginalData(articleData);
      setCurrentData(articleData);
      
    } catch (err) {
      console.error('Error loading article:', err);
      setError('Fehler beim Laden des Artikels');
      showError('Fehler beim Laden des Artikels');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (!currentData) return;

    setCurrentData(prev => ({
      ...prev!,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!currentData || !article?._id) return;

    setSaving(true);
    try {
      // Get supplier portal tokens
      const sessionToken = sessionStorage.getItem('supplierPortalSession');
      const masterToken = window.location.pathname.split('/')[3]; // Extract from URL path
      
      if (!sessionToken || !masterToken) {
        throw new Error('Supplier portal session not found');
      }

      if (article._id === 'new') {
        // Create new article
        console.log('[SupplierArticleDetailModal] Creating new article...');
        
        // Remove the _id from the data before creating
        const { _id, ...articleData } = currentData;
        
        const newArticle = await createSupplierArticle(masterToken, sessionToken, articleData);
        
        console.log('[SupplierArticleDetailModal] New article created:', newArticle.articleNumber);
        
        setData(newArticle);
        setOriginalData(newArticle);
        setCurrentData(newArticle);
        setMode('view');
        showSuccess('Artikel erfolgreich erstellt');
        
        // **WICHTIG: Update the article prop so the modal knows about the new ID**
        // This will trigger a re-render and reload the article data properly
        article._id = newArticle._id;
        article.articleNumber = newArticle.articleNumber;
        article.name = newArticle.name;
        
        // Notify parent component with new article
        if (onSave) {
          onSave(newArticle);
        }
      } else {
        // Update existing article
        console.log('[SupplierArticleDetailModal] Updating existing article...');
        
        const updatedArticle = await updateSupplierArticle(masterToken, sessionToken, article._id, currentData);
        setData(updatedArticle);
        setOriginalData(updatedArticle);
        setMode('view');
        showSuccess('Artikel erfolgreich gespeichert');
        
        // Notify parent component
        if (onSave) {
          onSave(updatedArticle);
        }
      }
    } catch (err) {
      console.error('Error saving article:', err);
      const errorMessage = article._id === 'new' ? 'Fehler beim Erstellen des Artikels' : 'Fehler beim Speichern des Artikels';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!currentData || !article?._id) return;

    setSaving(true);
    try {
      // Get supplier portal tokens
      const sessionToken = sessionStorage.getItem('supplierPortalSession');
      const masterToken = window.location.pathname.split('/')[3]; // Extract from URL path
      
      if (!sessionToken || !masterToken) {
        throw new Error('Supplier portal session not found');
      }

      // First save current changes
      if (hasDataChanged(originalData, currentData)) {
        await updateSupplierArticle(masterToken, sessionToken, article._id, currentData);
      }

      // Then submit using the workflow API (this will trigger email notification to admin)
      const modifiedFields = getModifiedFields(originalData, currentData);
      const result = await articleWorkflowApi.submitArticleToAdmin(article._id, {
        modifiedFields: Object.keys(modifiedFields),
        message: 'Artikel zur Prüfung eingereicht',
        requestId: article.requestId
      });

      if (result.success) {
        // Refresh the article data to get the updated status
        const updatedArticle = await getSupplierArticleById(masterToken, sessionToken, article._id);
        setData(updatedArticle);
        setOriginalData(updatedArticle);
        setCurrentData(updatedArticle);
        setMode('view');
        showSuccess('Artikel erfolgreich eingereicht');
        
        // Notify parent component
        if (onSave) {
          onSave(updatedArticle);
        }
      } else {
        throw new Error(result.error || 'Fehler beim Einreichen');
      }
    } catch (err) {
      console.error('Error submitting article:', err);
      showError('Fehler beim Einreichen des Artikels');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (article?._id === 'new') {
      // For new articles, close the modal instead of going to view mode
      handleClose();
    } else {
      // For existing articles, reset to original data and go to view mode
      if (originalData) {
        setCurrentData(originalData);
      }
      setMode('view');
    }
  };

  const handleClose = () => {
    setMode('view');
    setActiveTab('einkauf');
    setData(null);
    setCurrentData(null);
    setOriginalData(null);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-70"
        style={{ zIndex: 999999 }}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className="fixed inset-0 flex items-center justify-center pointer-events-none"
        style={{ zIndex: 999999 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl pointer-events-auto overflow-hidden flex flex-col"
          style={{ 
            width: '90vw', 
            maxWidth: '1000px', 
            height: '85vh',
            maxHeight: '800px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-xl">
                <FontAwesomeIcon icon={faBarcode} className="text-red-600 text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {data?._id === 'new' ? 'Neuen Artikel erstellen' : (() => {
                    const parts = [];
                    if (data?.brand) parts.push(data.brand);
                    if (data?.description) parts.push(data.description);
                    if (data?.description2) parts.push(data.description2);
                    if (data?.content && data?.contentUnit) {
                      parts.push(`${data.content} ${data.contentUnit}`);
                    } else if (data?.content) {
                      parts.push(data.content.toString());
                    }
                    return parts.length > 0 ? parts.join(' ') : 'Artikel Details';
                  })()}
                </h2>
                <p className="text-gray-600">
                  {data?._id === 'new' ? 'Füllen Sie die Artikeldaten aus und speichern Sie den neuen Artikel' : 
                   data?.gtin && `GTIN: ${data.gtin}`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {mode === 'edit' ? (
                <>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    {saving ? (
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                    ) : (
                      <FontAwesomeIcon icon={faSave} />
                    )}
                    {data?._id === 'new' ? 'Erstellen' : 'Speichern'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Abbrechen
                  </button>
                </>
              ) : (
                <>
                  {/* Show Submit button for existing articles that can be submitted */}
                  {data?._id !== 'new' && data?.status && ['draft', 'requested', 'needs_correction', 'rejected', 'in_progress'].includes(data.status) && (
                    <button
                      onClick={handleSubmitForReview}
                      disabled={saving}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      {saving ? (
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faSave} />
                      )}
                      Zur Prüfung einreichen
                    </button>
                  )}
                  
                  {/* Show Edit button for existing articles */}
                  {data?._id !== 'new' && (
                    <button
                      onClick={() => setMode('edit')}
                      className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      Bearbeiten
                    </button>
                  )}
                </>
              )}
              
              <button
                onClick={handleClose}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-6xl text-red-500 mb-4 animate-spin" />
                <p className="text-xl text-gray-600">
                  Artikel wird geladen...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xl text-red-600">Artikel konnte nicht geladen werden</p>
              </div>
            </div>
          ) : (
            <div className="flex h-[calc(95vh-120px)]">
              {/* Sidebar Navigation */}
              <div className="w-80 bg-gradient-to-b from-gray-50 to-gray-100 border-r border-gray-200 p-6">
                <nav className="space-y-3">
                  <button
                    onClick={() => setActiveTab('einkauf')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'einkauf' 
                        ? 'bg-red-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-3 text-lg" />
                    <span className="font-medium">Einkauf</span>
                  </button>
                  
                   <button
                    onClick={() => setActiveTab('einheiten')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'einheiten' 
                        ? 'bg-teal-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faRulerCombined} className="mr-3 text-lg" />
                    <span className="font-medium">Einheiten & Barcodes</span>
                  </button>
                  
                   <button
                    onClick={() => setActiveTab('vertrieb')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'vertrieb' 
                        ? 'bg-purple-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faTruck} className="mr-3 text-lg" />
                    <span className="font-medium">Vertrieb</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('marketing')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'marketing' 
                        ? 'bg-orange-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faTags} className="mr-3 text-lg" />
                    <span className="font-medium">Marketing</span>
                  </button>

                  <button
                    onClick={() => setActiveTab('naehrwerte')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'naehrwerte' 
                        ? 'bg-red-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faAppleAlt} className="mr-3 text-lg" />
                    <span className="font-medium">Nährwerte</span>
                  </button>
                  
                  <button
                    onClick={() => setActiveTab('fisch')}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 ${
                      activeTab === 'fisch' 
                        ? 'bg-cyan-600 text-white shadow-lg transform scale-105' 
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }`}
                  >
                    <FontAwesomeIcon icon={faFish} className="mr-3 text-lg" />
                    <span className="font-medium">Fisch</span>
                  </button>
                </nav>

                {/* Status Overview */}
                <div className="mt-8 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4 text-lg">Status-Übersicht</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FontAwesomeIcon icon={faUser} className="text-gray-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Lieferant</p>
                        <p className="text-xs text-gray-600">{data?.creditorNumber || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <FontAwesomeIcon icon={faEuroSign} className="text-gray-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Status</p>
                        <p className="text-xs text-gray-600">{data?.status || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <div className="p-8">
                  {activeTab === 'einkauf' && (
                    <ProductDataTab 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      preloadedEcrCodes={ecrCodes}
                      preloadedBrands={brands}
                      preloadedLabels={labels}
                      isSupplierMode={true}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                    />
                  )}
          
                  {activeTab === 'naehrwerte' && (
                    <ArticleDetailNaehrwerte 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      isSupplierMode={true}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                    />
                  )}
                  
                  {activeTab === 'fisch' && (
                    <FischTab 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      isSupplierMode={true}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                    />
                  )}
                  
                  {activeTab === 'einheiten' && (
                    <ArticleDetailEinheiten 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      isSupplierMode={true}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                    />
                  )}
                  
                  {activeTab === 'vertrieb' && (
                    <ArticleDetailVertrieb 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      isSupplierMode={true}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                    />
                  )}
                  
                  {activeTab === 'marketing' && (
                    <ArticleDetailMarketing 
                      data={currentData} 
                      mode={mode}
                      onChange={handleInputChange}
                      isSupplierMode={true}
                      originalData={originalData}
                      articleNumber={currentData?.articleNumber}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SupplierArticleDetailModal;
